import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { Workspace } from '@agency-smm/shared';
import { apiClient } from '../services/apiClient';
import { Button } from '../components/Button';
import { Modal, ModalActions } from '../components/Modal';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { useAuth } from '../hooks/useAuth';
import { CreateWorkspaceModal } from '../components/CreateWorkspaceModal';

const AVATAR_IMAGE_URL = '/novah_laugh.png';


/* ── Animations ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const popIn = keyframes`
  0%   { transform: scale(0); opacity: 0; }
  60%  { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
`;

/* ── Layout ── */
const DashboardLayout = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

/* ── Novah Center Area ── */
const NovahCenter = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl} 0;
  animation: ${fadeInUp} 0.5s ease-out;
`;

/* ── Orb animation keyframes ── */
const orbBlobA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  25% { transform: translate(15px, -20px) scale(1.15); }
  50% { transform: translate(-10px, 15px) scale(0.95); }
  75% { transform: translate(20px, 10px) scale(1.1); }
`;

const orbBlobB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1.1); }
  25% { transform: translate(-20px, 10px) scale(0.9); }
  50% { transform: translate(15px, -15px) scale(1.2); }
  75% { transform: translate(-10px, -20px) scale(1); }
`;

const orbBlobC = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(25px, 15px) scale(1.15); }
  66% { transform: translate(-15px, -10px) scale(0.9); }
`;

const orbGlowPulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
`;

const orbSheenMove = keyframes`
  0%, 100% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const NovahAvatarWrapper = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  animation: ${popIn} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
`;

/* Outer glow — very subtle ambient haze */
const OrbAmbient = styled.div`
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(180, 120, 255, 0.12) 0%,
    rgba(100, 180, 255, 0.06) 50%,
    transparent 70%
  );
  animation: ${orbGlowPulse} 4s ease-in-out infinite;
  z-index: 0;
`;

/* Main orb container — thin ring around avatar */
const OrbSphere = styled.div`
  position: absolute;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  overflow: hidden;
  z-index: 1;
  background: transparent;
  box-shadow:
    0 0 30px rgba(180, 100, 255, 0.2),
    0 0 60px rgba(140, 80, 230, 0.08);
`;

/* Animated blob layers — subtle color wisps */
const OrbBlob = styled.div<{ $color: string; $size: number; $top: string; $left: string }>`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  filter: blur(20px);
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};
`;

const BlobA = styled(OrbBlob)`
  animation: ${orbBlobA} 8s ease-in-out infinite;
`;

const BlobB = styled(OrbBlob)`
  animation: ${orbBlobB} 10s ease-in-out infinite;
`;

const BlobC = styled(OrbBlob)`
  animation: ${orbBlobC} 7s ease-in-out infinite;
`;

/* Glass reflection — small highlight */
const OrbSheen = styled.div`
  position: absolute;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  z-index: 3;
  pointer-events: none;
  background: radial-gradient(circle at 30% 25%, rgba(255,255,255,0.25) 0%, transparent 20%);
  animation: ${orbSheenMove} 25s linear infinite;
`;

/* Thin elegant rim */
const OrbRim = styled.div`
  position: absolute;
  width: 170px;
  height: 170px;
  border-radius: 50%;
  z-index: 4;
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const NovahAvatar = styled.div`
  position: relative;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  overflow: hidden;
  z-index: 5;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  /* Gentle edge blend */
  mask-image: radial-gradient(circle, #000 65%, rgba(0,0,0,0.4) 82%, transparent 100%);
  -webkit-mask-image: radial-gradient(circle, #000 65%, rgba(0,0,0,0.4) 82%, transparent 100%);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NovahGreeting = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  margin: 0 0 8px 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.fontSize.xxl};
  }
`;

const NovahSubtext = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
  max-width: 400px;
  line-height: 1.6;
`;

const QuickActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const ActionCard = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: ${({ $variant }) =>
    $variant === 'primary' ? 'none' : '1px solid #E2E8F0'};
  background: ${({ $variant }) =>
    $variant === 'primary'
      ? 'linear-gradient(135deg, #3B82F6, #2563EB)'
      : '#FFFFFF'};
  color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#0F172A')};
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${({ $variant }) =>
    $variant === 'primary'
      ? '0 4px 14px rgba(59, 130, 246, 0.35)'
      : '0 1px 3px rgba(0,0,0,0.06)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ $variant }) =>
      $variant === 'primary'
        ? '0 6px 20px rgba(59, 130, 246, 0.45)'
        : '0 4px 12px rgba(0,0,0,0.1)'};
  }

  span.icon {
    font-size: 20px;
  }
`;

const RecentActivity = styled.div`
  width: 100%;
  max-width: 500px;
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const ActivityTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  text-align: center;
`;

const TipCard = styled.div`
  background: linear-gradient(135deg, #EFF6FF, #F0F9FF);
  border: 1px solid #BFDBFE;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #1E40AF;
  line-height: 1.5;
`;

/* ── (workspace panel removed — accessible via sidebar) ── */

const TIPS = [
  'Tip: Add a logo to each workspace for branded templates',
  'Tip: Upload background photos for more diverse post images',
  'Tip: Use content mix to balance education, connection & promotion',
  'Tip: Try generating for multiple platforms at once to save time',
];

export function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspaces, addWorkspace, removeWorkspace } = useWorkspaces();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [workspaceToDelete, setWorkspaceToDelete] = useState<Workspace | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      setIsModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleWorkspaceCreated = (workspace: Workspace) => {
    addWorkspace(workspace);
    setIsModalOpen(false);
    navigate(`/workspace/${workspace.id}/generate`);
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceToDelete) return;
    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/workspaces/${workspaceToDelete.id}`);
      removeWorkspace(workspaceToDelete.id);
      setWorkspaceToDelete(null);
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      alert('Failed to delete workspace. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'there';
  const totalPosts = workspaces.reduce((sum, w) => sum + (w.postsCount || 0), 0);
  const tip = TIPS[Math.floor(Date.now() / 86400000) % TIPS.length];
  const lastWorkspace = workspaces[0];

  return (
    <>
      <DashboardLayout>
        {/* ── Novah Center ── */}
        <NovahCenter>
          {isModalOpen ? (
            /* Inline wizard — replaces hero when creating workspace */
            <CreateWorkspaceModal
              isOpen={true}
              inline
              onClose={() => setIsModalOpen(false)}
              onCreated={handleWorkspaceCreated}
            />
          ) : (
            <>
              <NovahAvatarWrapper>
                <OrbAmbient />
                <OrbSphere>
                  <BlobA $color="rgba(255, 130, 200, 0.5)" $size={100} $top="5%" $left="10%" />
                  <BlobB $color="rgba(150, 100, 255, 0.4)" $size={90} $top="35%" $left="45%" />
                  <BlobC $color="rgba(80, 200, 230, 0.35)" $size={85} $top="50%" $left="10%" />
                </OrbSphere>
                <OrbSheen />
                <OrbRim />
                <NovahAvatar>
                  <img src={AVATAR_IMAGE_URL} alt="Novah" />
                </NovahAvatar>
              </NovahAvatarWrapper>

              <NovahGreeting>Hey {firstName}!</NovahGreeting>
              <NovahSubtext>
                {workspaces.length === 0
                  ? "I'm Novah, your brand assistant. Let's create your first workspace to get started!"
                  : `You have ${workspaces.length} workspace${workspaces.length !== 1 ? 's' : ''} and ${totalPosts} post${totalPosts !== 1 ? 's' : ''} generated. What would you like to do?`}
              </NovahSubtext>

              <QuickActions>
                <ActionCard $variant="primary" onClick={() => setIsModalOpen(true)}>
                  <span className="icon">+</span>
                  New Workspace
                </ActionCard>
                {lastWorkspace && (
                  <ActionCard onClick={() => navigate(`/workspace/${lastWorkspace.id}`)}>
                    <span className="icon">
                      {lastWorkspace.logoUrl ? (
                        <img src={lastWorkspace.logoUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : '📂'}
                    </span>
                    Open {lastWorkspace.name.length > 15 ? lastWorkspace.name.substring(0, 15) + '...' : lastWorkspace.name}
                  </ActionCard>
                )}
              </QuickActions>

              <RecentActivity>
                <ActivityTitle>Daily tip</ActivityTitle>
                <TipCard>{tip}</TipCard>
              </RecentActivity>
            </>
          )}
        </NovahCenter>

      </DashboardLayout>


      <Modal
        isOpen={!!workspaceToDelete}
        onClose={() => setWorkspaceToDelete(null)}
        title="Delete Workspace?"
        titleVariant="error"
        closeOnOverlayClick={!isDeleting}
        closeOnEscape={!isDeleting}
      >
        <p style={{ color: '#525252', marginBottom: '16px', lineHeight: 1.5 }}>
          Are you sure you want to delete &quot;{workspaceToDelete?.name}&quot;? This will permanently delete:
        </p>
        <ul style={{ marginTop: '12px', paddingLeft: '20px', color: '#525252' }}>
          <li>All brand information and research</li>
          <li>All brand assets and images</li>
          <li>All generated posts ({workspaceToDelete?.postsCount || 0} posts)</li>
          <li>All export history</li>
        </ul>
        <strong style={{ display: 'block', marginTop: '12px', color: '#EF4444' }}>
          This action cannot be undone.
        </strong>
        <ModalActions>
          <Button type="button" $variant="outline" onClick={() => setWorkspaceToDelete(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button type="button" onClick={handleDeleteWorkspace} disabled={isDeleting} $variant="danger">
            {isDeleting ? 'Deleting...' : 'Delete Workspace'}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
